/**
 * Assessment Sync Middleware
 *
 * Automatically syncs legacy Redux actions to the new assessment-centric slices.
 * This enables gradual migration without requiring all components to be updated at once.
 *
 * Strategy:
 * - Intercepts legacy actions (RISK_*, RESIDUAL_*, SECURITY_*, CONTEXT_*)
 * - After legacy reducer processes the action, dispatches equivalent new slice action
 * - Both old and new state stay in sync during migration period
 *
 * @see /Users/agaziel/.claude/plans/elegant-hatching-pnueli.md for full migration plan
 */

import { updateResidualRisk, updateAssessmentContext } from '../slices/assessmentsSlice';
// Note: updateRiskFormData and updateSecurityScenarios will be used when we add handlers for those action types

/**
 * Maps legacy action types to their sync handlers
 * Each handler returns the new slice action to dispatch, or null to skip sync
 */
const syncHandlers = {
  // Residual Risk Actions
  'SAVE_RESIDUAL_RISK_SUCCESS': (action, state) => {
    const { modelId, riskId, ...data } = action.payload || {};
    const assessmentUuid = getAssessmentUuid(state, modelId);

    if (!assessmentUuid || !riskId) return null;

    return updateResidualRisk({
      assessmentUuid,
      riskId,
      data: {
        residual_rating: data.residual_rating,
        residual_explanation: data.residual_explanation,
        action_plan_rating: data.action_plan_rating,
        action_plan_analysis: data.action_plan_analysis,
        action_plan_short_summary: data.action_plan_short_summary
      }
    });
  },

  'GENERATE_RESIDUAL_RISK_SUCCESS': (action, state) => {
    const { modelId, riskId, residual_rating, residual_explanation } = action.payload || {};
    const assessmentUuid = getAssessmentUuid(state, modelId);

    if (!assessmentUuid || !riskId) return null;

    return updateResidualRisk({
      assessmentUuid,
      riskId,
      data: { residual_rating, residual_explanation }
    });
  },

  'ANALYZE_ACTION_PLAN_SUCCESS': (action, state) => {
    const { modelId, riskId, action_plan_rating, action_plan_analysis, action_plan_short_summary } = action.payload || {};
    const assessmentUuid = getAssessmentUuid(state, modelId);

    if (!assessmentUuid || !riskId) return null;

    return updateResidualRisk({
      assessmentUuid,
      riskId,
      data: { action_plan_rating, action_plan_analysis, action_plan_short_summary }
    });
  },

  // Context Actions - sync narrative updates
  'SET_CONTEXT_NARRATIVE': (action, state) => {
    const { modelId, section, narrative } = action.payload || {};
    const assessmentUuid = getAssessmentUuid(state, modelId);

    if (!assessmentUuid || !section) return null;

    return updateAssessmentContext({
      assessmentUuid,
      section,
      data: { narrative }
    });
  },

  // Note: Risk form data sync is more complex due to nested structure
  // We handle specific high-impact actions rather than trying to sync everything
  'SET_RISK_PRIORITY_SUCCESS': (action, state) => {
    // This updates priority on a specific risk
    // For now, we sync through the component migration rather than middleware
    // because riskFormData structure is deeply nested
    return null;
  }
};

/**
 * Helper to find assessmentUuid from modelId
 * Checks multiple sources in priority order
 */
function getAssessmentUuid(state, modelId) {
  // 1. Check app.currentAssessmentUuid if current model matches
  if (state.app?.currentModelId === modelId && state.app?.currentAssessmentUuid) {
    return state.app.currentAssessmentUuid;
  }

  // 2. Search risks.byAssessment for matching modelId
  const byAssessment = state.risks?.byAssessment || {};
  for (const [uuid, assessment] of Object.entries(byAssessment)) {
    if (assessment.modelId === modelId) {
      return uuid;
    }
  }

  // 3. Search assessments.byId for matching model_uuid
  const assessments = state.assessments?.byId || {};
  for (const [uuid, assessment] of Object.entries(assessments)) {
    if (assessment.model_uuid === modelId) {
      return uuid;
    }
  }

  return null;
}

/**
 * The middleware itself
 */
const assessmentSyncMiddleware = (store) => (next) => (action) => {
  // Let the action process through reducers first
  const result = next(action);

  // Check if we have a sync handler for this action type
  const handler = syncHandlers[action.type];
  if (handler) {
    const state = store.getState();
    const syncAction = handler(action, state);

    if (syncAction) {
      // Dispatch the sync action to update new slice
      // Use a try-catch to prevent sync errors from breaking the app
      try {
        store.dispatch(syncAction);

        if (process.env.NODE_ENV === 'development') {
          console.log('[AssessmentSync] Synced action:', action.type, '→', syncAction.type);
        }
      } catch (error) {
        console.error('[AssessmentSync] Failed to sync action:', action.type, error);
      }
    }
  }

  return result;
};

export default assessmentSyncMiddleware;
